import React, { useState, useRef, useEffect } from 'react';
import { X, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

const MultiSelectAutocomplete = ({
  options = {},
  value = [],
  onChange,
  placeholder = 'Select languages...',
  maxSelections = null,
  searchPlaceholder = 'Search...',
  onMaxLimitReached,
  testId = 'multi-select-autocomplete'
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  // Filter options based on search term
  const filteredOptions = Object.entries(options).filter(([code, langInfo]) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      code.toLowerCase().includes(searchLower) ||
      langInfo.native_name?.toLowerCase().includes(searchLower) ||
      langInfo.name?.toLowerCase().includes(searchLower) ||
      langInfo.flag?.includes(searchTerm)
    );
  });

  // Handle outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (code) => {
    if (maxSelections && value.length >= maxSelections && !value.includes(code)) {
      if (onMaxLimitReached) {
        onMaxLimitReached();
      }
      return;
    }

    const newValue = value.includes(code)
      ? value.filter(v => v !== code)
      : [...value, code];

    // Ensure at least one item selected
    const finalValue = newValue.length > 0 ? newValue : value;
    onChange(finalValue);
  };

  const handleRemove = (code, e) => {
    e.stopPropagation();
    const newValue = value.filter(v => v !== code);
    // Ensure at least one item selected
    const finalValue = newValue.length > 0 ? newValue : value;
    onChange(finalValue);
  };

  return (
    <div ref={containerRef} className="relative w-full">
      {/* Selected items display */}
      <div
        onClick={() => setIsOpen(!isOpen)}
        className="w-full min-h-[44px] px-3 py-2 border border-gray-300 rounded-lg bg-white cursor-pointer flex flex-wrap gap-2 items-center hover:border-gray-400 transition-colors"
        data-testid={testId}
      >
        {value.length > 0 ? (
          <>
            {value.map((code) => {
              const langInfo = options[code];
              return (
                <div
                  key={code}
                  className="flex items-center gap-1 bg-purple-100 text-purple-700 px-3 py-1 rounded-full text-sm font-medium"
                >
                  <span>{langInfo?.flag || ''}</span>
                  <span>{langInfo?.native_name || code}</span>
                  <button
                    onClick={(e) => handleRemove(code, e)}
                    className="ml-1 hover:bg-purple-200 rounded-full p-0.5 transition-colors"
                    aria-label={`Remove ${langInfo?.native_name || code}`}
                  >
                    <X size={14} />
                  </button>
                </div>
              );
            })}
            <ChevronDown
              size={16}
              className={`ml-auto text-gray-500 transition-transform ${
                isOpen ? 'transform rotate-180' : ''
              }`}
            />
          </>
        ) : (
          <>
            <span className="text-gray-400">{placeholder}</span>
            <ChevronDown
              size={16}
              className={`ml-auto text-gray-500 transition-transform ${
                isOpen ? 'transform rotate-180' : ''
              }`}
            />
          </>
        )}
      </div>

      {/* Dropdown menu */}
      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-300 rounded-lg shadow-lg z-50">
          {/* Search input */}
          <div className="p-2 border-b border-gray-200">
            <input
              ref={inputRef}
              type="text"
              placeholder={searchPlaceholder}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              autoFocus
            />
          </div>

          {/* Options list */}
          <div className="max-h-[280px] overflow-y-auto w-full">
            {filteredOptions.length > 0 ? (
              filteredOptions.map(([code, langInfo]) => (
                <div
                  key={code}
                  onClick={() => handleSelect(code)}
                  className={`w-full px-3 py-2 cursor-pointer flex items-center gap-2 transition-colors ${
                    value.includes(code)
                      ? 'bg-purple-50 border-l-4 border-purple-500'
                      : 'hover:bg-gray-50'
                  } ${
                    maxSelections && value.length >= maxSelections && !value.includes(code)
                      ? 'opacity-50 cursor-not-allowed'
                      : ''
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={value.includes(code)}
                    onChange={() => {}}
                    className="flex-shrink-0 !h-4 !w-4 rounded border-gray-300"
                    disabled={
                      maxSelections && value.length >= maxSelections && !value.includes(code)
                    }
                  />
                  <span className="text-lg flex-shrink-0">{langInfo.flag}</span>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-gray-900 truncate">{langInfo.native_name}</div>
                    <div className="text-xs text-gray-500 truncate">{langInfo.name}</div>
                  </div>
                </div>
              ))
            ) : (
              <div className="px-4 py-8 text-center text-gray-500 text-sm">
                No languages found
              </div>
            )}
          </div>

          {/* Footer info */}
          {maxSelections && (
            <div className="px-4 py-2 border-t border-gray-200 text-xs text-gray-500 bg-gray-50">
              {value.length >= maxSelections
                ? t('settings.maxSelections', { count: maxSelections })
                : t('settings.selectUpTo', { count: maxSelections })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MultiSelectAutocomplete;
